import { handleResponseOld, makeGet, makePost } from './helpers';

const transformSellerRating = (payload) => {
    const rating = payload.payload || {};
    return {
        [rating.houseId]: rating,
    };
};

const transformHelpfulCount = (body) => {
    if (Array.isArray(body.payload)) {
        const val = body.payload.map((item) => {
            return { [item.reviewId]: item.count };
        });
        return Object.assign({}, ...val);
    }
};

const transformReviewComments = (body) => {
    if (body.payload && Boolean(body.payload.length)) {
        const arr = { [body.payload[0].houseId]: body.payload };
        return arr;
    } else {
        return [];
    }
};

const transformBidderStatuses = (body) => {
    if (Array.isArray(body.payload)) {
        const val = body.payload.map((item) => {
            return { [item.reviewId]: item.flags };
        });
        return Object.assign({}, ...val);
    }
};

/**
 * @category Review API
 * @see fetchBidderStatuses
 */
type FetchBidderStatusesParams = {
    authToken: string;
    deployment: string;
    reviewIds: any;
};

/**
 * @category Review API
 * @see fetchHelpfulCounts
 */
type FetchHelpfulCountsParams = {
    authToken?: string;
    deployment: string;
    reviewIds: any;
};

/**
 * @category Review API
 * @see fetchHouseComments
 */
type FetchHouseCommentsParams = {
    authToken: string;
    deployment: string;
    houseId: number;
};

/**
 * @category Review API
 * @see fetchHouseReviewData
 */
type FetchHouseReviewDataParams = {
    authToken: string;
    deployment: string;
    filter: string;
    houseId: number;
    page: number;
    pageSize: number;
    sort: string;
};

/**
 * @category Review API
 * @see fetchHousePercentiles
 */
type FetchHousePercentilesParams = {
    authToken: string;
    deployment: string;
    houseId: number;
};

/**
 * @category Review API
 * @see fetchReviewComments
 */
type FetchReviewCommentsParams = {
    authToken: string;
    deployment: string;
    reviewId: any;
};

/**
 * @category Review API
 * @see setBidderReviewStatus
 */
type SetReviewStatusParams = {
    authToken: string;
    deployment: string;
    reviewStatus: any;
};

/**
 * @category Review API
 * @see submitReview
 */
type SubmitReviewParams = {
    authToken: string;
    deployment: string;
    review: any;
};

/**
 * gets Review Status from Review API
 * @category Review API
 * @function fetchBidderStatuses
 * @param {FetchBidderStatusesParams}
 * @see https://api.liveauctioneers.com/review/getbidderreviewstatus/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/7fac16/methods/GET
 */
const fetchBidderStatuses = ({ authToken, deployment, reviewIds }: FetchBidderStatusesParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'getbidderreviewstatus',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        const ids = `[${reviewIds.join(',')}]`;
        request.query({ reviewIds: ids });
        request.end((err, response) =>
            handleResponseOld({ err, reject, resolve, response, transform: transformBidderStatuses })
        );
    });

/**
 * gets Helpful Counts from Review API
 * @category Review API
 * @function fetchHelpfulCounts
 * @param {FetchHelpfulCountsParams}
 * @see https://api.liveauctioneers.com/review/gethelpfulcount/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/i908fd/methods/GET
 */
const fetchHelpfulCounts = ({ deployment, reviewIds }: FetchHelpfulCountsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'gethelpfulcount',
            deployment,
            path: '<REVIEW-API>',
        });
        const ids = `[${reviewIds.join(',')}]`;
        request.query({ reviewIds: ids });
        request.end((err, response) =>
            handleResponseOld({ err, reject, resolve, response, transform: transformHelpfulCount })
        );
    });

/**
 * gets public only house comments from the Review API
 * @category Review API
 * @function fetchHouseComments
 * @param {FetchHelpfulCountsParams}
 * @see https://api.liveauctioneers.com/review/GetHouseReviewComments/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/u0wivx/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/get_review_GetHouseReviewComments
 */
const fetchHouseComments = ({ authToken, deployment, houseId }: FetchHouseCommentsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'GetHouseReviewComments',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        request.query({ houseId, publicOnly: true });
        request.end((err, response) =>
            handleResponseOld({ err, reject, resolve, response, transform: transformReviewComments })
        );
    });

/**
 * gets the House Percentile parameters from the Review API
 * @category Review API
 * @function fetchHousePercentiles
 * @param {FetchHousePercentilesParams}
 * @see https://api.liveauctioneers.com/review/rank/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/tf3pmu/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/get_review_rank
 */
const fetchHousePercentiles = ({ authToken, deployment, houseId }: FetchHousePercentilesParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'rank',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        request.query({ houseId });
        request.end((err, response) =>
            handleResponseOld({ err, reject, resolve, response, transform: transformSellerRating })
        );
    });

/**
 * gets the house review data from the Review API
 * @category Review API
 * @function fetchHouseReviewData
 * @param {FetchHouseReviewDataParams}
 * @see https://api.liveauctioneers.com/review/rating/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/g4khl5/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/get_review_rating
 */
const fetchHouseReviewData = ({
    authToken,
    deployment,
    filter = '',
    houseId,
    page = 1,
    pageSize = 50,
    sort = '-overall',
}: FetchHouseReviewDataParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'rating',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });

        request.query({ filter, houseId, page, pageSize, sort });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * gets house review specific comments from the Review API
 * @category Review API
 * @function fetchReviewComments
 * @param {FetchReviewCommentsParams}
 * @see https://api.liveauctioneers.com/review/GetHouseReviewComments/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/u0wivx/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/get_review_GetHouseReviewComments
 */
const fetchReviewComments = ({ authToken, deployment, reviewId }: FetchReviewCommentsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'GetHouseReviewComments',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        request.query({ reviewId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Review API
 * @see fetchReviewData
 */
type FetchReviewDataParams = {
    authToken: string;
    bidderId: number;
    catalogIds: any;
    deployment: string;
};

type FetchReviewDataResponse = {
    error: boolean;
    payload: [
        {
            catID: number;
            lots: number[];
            review: {
                ReviewSubmittedTS: number;
                accuracy: number;
                bidderCity: string;
                bidderCountry: string;
                bidderFirstName: string;
                bidderId: number;
                bidderLastName: string;
                bidderState: string;
                catalogId: number;
                overall: number;
                payment: number;
                responsiveness: number;
                review: string;
                reviewHeadline: string;
                reviewId: number;
                reviewSubmitted: string;
                reviewSubmittedTs: number;
                sellerId: number;
                shipping: number;
                wonItems: any;
            };
            reviewed: boolean;
        }
    ];
};

/**
 * gets Review information from the Review API
 * @category Review API
 * @function fetchReviewData
 * @param {FetchReviewDataParams}
 * @see https://api.liveauctioneers.com/review/GetReviewInformation/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/8cefhw/methods/GET
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/get_review_GetReviewInformation
 */
const fetchReviewData = ({ authToken, bidderId, catalogIds, deployment }: FetchReviewDataParams) =>
    new Promise<FetchReviewDataResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: 'GetReviewInformation',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        request.query({ bidderId, catalogs: `${catalogIds}` });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * posts bidder review status to the Review API
 * @category Review API
 * @function setBidderReviewStatus
 * @param {SetReviewStatusParams}
 * @see https://api.liveauctioneers.com/review/setreviewstatus/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/2772za/methods/POST
 */
const setBidderReviewStatus = ({ authToken, deployment, reviewStatus }: SetReviewStatusParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ apiPath: 'setreviewstatus', authToken, deployment, path: '<REVIEW-API>' });
        request.send(reviewStatus);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * posts review data to the Review API
 * @category Review API
 * @function submitReview
 * @param {SubmitReviewParams}
 * @see https://api.liveauctioneers.com/review/createreview/DEPLOYMENT
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2kf0hougo6/resources/gn5r8f/methods/POST
 * @see https://app.swaggerhub.com/apis/LiveAuctioneers/ReviewService/1.0.0#/default/post_review_createreview
 */
const submitReview = ({ authToken, deployment, review }: SubmitReviewParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: 'createreview',
            authToken,
            deployment,
            path: '<REVIEW-API>',
        });
        request.send(review);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    fetchBidderStatuses,
    fetchHelpfulCounts,
    fetchHouseComments,
    fetchHousePercentiles,
    fetchHouseReviewData,
    fetchReviewComments,
    fetchReviewData,
    setBidderReviewStatus,
    submitReview,
};
